import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { DateRangePicker, createStaticRanges } from "react-date-range";
import { startOfDay, endOfDay, subDays, isSameDay } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { FaCalendar } from "react-icons/fa";

const getTodayRange = () => ({
  key: "selection",
  keyName: "today",
  startDate: startOfDay(new Date()),
  endDate: endOfDay(new Date()),
});

const getAppliedRange = (value) => {
  if (!value?.startDate || !value?.endDate) {
    return null;
  }

  return {
    startDate: new Date(value.startDate),
    endDate: new Date(value.endDate),
    key: "selection",
    keyName: value.keyName || value.key || "custom",
  };
};

const customStaticRanges = createStaticRanges([
  {
    label: "All Time",
    range: () => ({
      keyName: "allTime",
      startDate: new Date("2010-01-01"),
      endDate: new Date(),
    }),
  },
  {
    label: "Today",
    range: () => getTodayRange(),
  },
  {
    label: "Yesterday",
    range: () => ({
      keyName: "yesterday",
      startDate: startOfDay(subDays(new Date(), 1)),
      endDate: endOfDay(subDays(new Date(), 1)),
    }),
  },
]);

export default function ProductHistoryDateRangeInput({
  onSelect,
  value,
  containerClassName = "",
  inputClassName = "",
}) {
  const appliedRange = getAppliedRange(value);
  const [open, setOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  const [range, setRange] = useState(() => appliedRange || getTodayRange());
  const [isMobile, setIsMobile] = useState(false);
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 12,
    maxWidth: 0,
    maxHeight: 0,
  });
  const triggerRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    setRange(getAppliedRange(value) || getTodayRange());
    setPendingStart(null);
  }, [value]);

  const handleSelect = (ranges) => {
    const selected = ranges.selection;
    const isFirstClick =
      !pendingStart ||
      selected.endDate.getTime() === pendingStart.getTime() ||
      selected.endDate.getTime() === selected.startDate.getTime();

    if (isFirstClick) {
      setPendingStart(selected.startDate);

      setRange({
        startDate: selected.startDate,
        endDate: selected.endDate,
        key: "selection",
        keyName: "custom",
        isIncomplete: true,
      });

      return;
    }

    // second click
    const matchedStatic = customStaticRanges.find((r) => {
      const rangeDates = r.range();
      return (
        isSameDay(selected.startDate, rangeDates.startDate) &&
        isSameDay(selected.endDate, rangeDates.endDate)
      );
    });

    const finalRange = {
      startDate: pendingStart,
      endDate: selected.endDate,
      key: "selection",
      keyName: matchedStatic ? matchedStatic.range().keyName : "custom",
    };

    setPendingStart(null);
    setRange(finalRange);
  };

  const handleClose = () => {
    setRange(getAppliedRange(value) || getTodayRange());
    setPendingStart(null);
    setOpen(false);
  };

  const handleApply = () => {
    if (onSelect) onSelect(range);
    setOpen(false);
  };

  const handleClear = () => {
    setRange(getTodayRange());
    setPendingStart(null);
    if (onSelect) onSelect(null);
    setOpen(false);
  };

  const syncPopupPosition = () => {
    if (typeof window === "undefined" || !triggerRef.current) {
      return;
    }

    const viewportPadding = 12;
    const estimatedPopupWidth = 920;
    const rect = triggerRef.current.getBoundingClientRect();
    const maxWidth = window.innerWidth - viewportPadding * 2;
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      Math.max(
        viewportPadding,
        window.innerWidth - estimatedPopupWidth - viewportPadding,
      ),
    );
    const top = rect.bottom + 8;

    setPopupPosition({
      top,
      left,
      maxWidth,
      maxHeight: Math.max(320, window.innerHeight - top - viewportPadding),
    });
  };

  // Format display text for input
  const formatted = appliedRange
    ? `${appliedRange.startDate.toLocaleDateString()} - ${appliedRange.endDate.toLocaleDateString()}`
    : "";

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        popupRef.current?.contains(event.target)
      ) {
        return;
      }

      setRange(getAppliedRange(value) || getTodayRange());
      setPendingStart(null);
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize(); // initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!open || isMobile) {
      return undefined;
    }

    syncPopupPosition();
    const handlePositionChange = () => syncPopupPosition();

    window.addEventListener("resize", handlePositionChange);
    window.addEventListener("scroll", handlePositionChange, true);

    return () => {
      window.removeEventListener("resize", handlePositionChange);
      window.removeEventListener("scroll", handlePositionChange, true);
    };
  }, [open, isMobile]);

  const popupContent = (
    <div
      ref={popupRef}
      className="flex max-h-full w-max max-w-full flex-col overflow-hidden rounded-lg bg-white shadow-lg"
    >
      <div className="max-w-full overflow-auto">
        <DateRangePicker
          displayMode="dateRange"
          dragSelectionEnabled
          onChange={handleSelect}
          ranges={[range]}
          staticRanges={customStaticRanges}
          inputRanges={[]}
          showSelectionPreview
          moveRangeOnFirstSelection={false}
          direction={isMobile ? "vertical" : "horizontal"}
          months={isMobile ? 1 : 2}
          className="w-max p-2"
        />
      </div>
      <div className="flex shrink-0 flex-row items-center justify-between border-t border-gray-200 p-2">
        <button
          onClick={handleClear}
          className="btn btn-light btn-sm rounded bg-gray-100 px-3 py-1 text-dblack hover:bg-gray-200"
        >
          Clear
        </button>
        <div className="flex flex-row justify-end">
          <button
            onClick={handleApply}
            className="btn btn-primary btn-sm rounded bg-blue-600 px-3 py-1 text-dblue hover:bg-blue-700"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  const desktopPopupMarkup =
    open && !isMobile && typeof document !== "undefined"
      ? createPortal(
          <div
            style={{
              position: "fixed",
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
              maxWidth: `${popupPosition.maxWidth}px`,
              maxHeight: `${popupPosition.maxHeight}px`,
              zIndex: 100,
            }}
            className="overflow-auto"
          >
            {popupContent}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={triggerRef}
      className={`relative w-full ${containerClassName}`.trim()}
    >
      {/* Input */}
      <input
        type="text"
        readOnly
        value={formatted}
        placeholder="Select date"
        onClick={() => {
          if (open) {
            handleClose();
            return;
          }

          setOpen(true);
        }}
        className={`h-10 w-full cursor-pointer rounded-md border border-dgrey1 px-2  text-sm font-normal outline-none placeholder:text-[#b3afaf] ${inputClassName}`.trim()}
      />

      {open && isMobile ? (
        <div className="absolute left-0 z-50 mt-2 w-full max-w-full overflow-auto">
          {popupContent}
        </div>
      ) : null}
      {desktopPopupMarkup}
    </div>
  );
}
